/**
 * CoachPlaybook - ToolStrip
 * Version: 0.6.0
 * Description: Minimal vertical toolbar for Playbook Studio surface tools
 */

import React, { useState, useEffect, useRef } from 'react';

const ACCENT = '#3b82f6';
const ACCENT_HOVER = '#2563eb';

const icons = {
  select: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path
        d="M3 1L3 12L6.5 8.5L10 13L12 12L8.5 7.5L13 7.5L3 1Z"
        fill="currentColor"
      />
    </svg>
  ),
  move: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path d="M8 2L10 4.5H9V7H11.5V5.5L14 8L11.5 10.5V9H9V11.5H10L8 14L6 11.5H7V9H4.5V10.5L2 8L4.5 5.5V7H7V4.5H6L8 2Z"
        fill="currentColor" />
    </svg>
  ),
  eraser: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path
        d="M10.5 2L14 5.5L7.5 12H3L1.5 10.5L10.5 2Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <line x1="6" y1="6.5" x2="10" y2="10.5" stroke="currentColor" strokeWidth="1.5" />
      <line x1="3" y1="14" x2="13" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  undo: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 7L2 5L4 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 5H10C12.2091 5 14 6.79086 14 9C14 11.2091 12.2091 13 10 13H7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  redo: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path
        d="M12 7L14 5L12 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 5H6C3.79086 5 2 6.79086 2 9C2 11.2091 3.79086 13 6 13H9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  shield: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 1.5L2.5 4V8C2.5 11.5 8 14.5 8 14.5C8 14.5 13.5 11.5 13.5 8V4L8 1.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  ),
  grid: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="12" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <line x1="6" y1="2" x2="6" y2="14" stroke="currentColor" strokeWidth="1.5" />
      <line x1="10" y1="2" x2="10" y2="14" stroke="currentColor" strokeWidth="1.5" />
      <line x1="2" y1="6" x2="14" y2="6" stroke="currentColor" strokeWidth="1.5" />
      <line x1="2" y1="10" x2="14" y2="10" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  gear: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 1V3M8 13V15M1 8H3M13 8H15M3.05 3.05L4.46 4.46M11.54 11.54L12.95 12.95M12.95 3.05L11.54 4.46M4.46 11.54L3.05 12.95"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
};

const styles = {
  strip: {
    position: 'absolute',
    left: 6,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 46,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    padding: '5px 3px',
    background: 'rgba(15, 23, 42, 0.85)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: 10,
    border: '1px solid rgba(255, 255, 255, 0.08)',
    zIndex: 20,
    userSelect: 'none',
  },
  divider: {
    width: 18,
    height: 1,
    background: 'rgba(255, 255, 255, 0.1)',
    margin: '1px 0',
    flexShrink: 0,
  },
  // Collapsed tap-target on mobile — pinned to the bottom-left of the court,
  // but ABOVE the timeline scrubber (which sits at bottom:0 of surfaceArea)
  // so they don't overlap. `bottom: 68` = 60px timeline + 8px gap.
  collapsedButton: {
    width: 46,
    height: 46,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#f97316',
    cursor: 'pointer',
    top: 'auto',
    bottom: 68,
    left: 8,
    transform: 'none',
    background: 'linear-gradient(180deg, rgba(15,23,42,0.94), rgba(9,10,15,0.98))',
    border: '1px solid rgba(249,115,22,0.35)',
    boxShadow: '0 6px 18px rgba(0,0,0,0.5), 0 0 0 3px rgba(249,115,22,0.12)',
  },
};

const getButtonStyle = (isActive, isDisabled, isHovered) => ({
  width: 40,
  height: 40,
  borderRadius: 12,
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: isDisabled ? 'default' : 'pointer',
  color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.7)',
  background: isActive
    ? ACCENT
    : isHovered && !isDisabled
      ? 'rgba(255, 255, 255, 0.12)'
      : 'rgba(255, 255, 255, 0.06)',
  opacity: isDisabled ? 0.3 : 1,
  transition: 'background 0.15s, opacity 0.15s, color 0.15s',
  padding: 0,
  flexShrink: 0,
});

const ToolButton = ({ id, icon, active, disabled, title, onClick }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <button
      style={getButtonStyle(active, disabled, hovered)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => !disabled && onClick(id)}
      title={title}
      disabled={disabled}
    >
      {icon}
    </button>
  );
};

const ToolStrip = ({
  activeTool = 'select',
  showDefenders = true,
  showGrid = false,
  canUndo = false,
  canRedo = false,
  hideDefenderToggle = false,
  onToolChange = () => {},
  onToggleDefenders = () => {},
  onToggleGrid = () => {},
  onUndo = () => {},
  onRedo = () => {},
  onSettings,
}) => {
  // Mobile-collapse: strip covers court space on phones. Collapse to a single
  // tap-target showing the active tool icon; tap to reveal full strip;
  // auto-collapse ~350ms after any tool button is used.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 640
  );
  const [expanded, setExpanded] = useState(false);
  const collapseTimeoutRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => () => {
    if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
  }, []);

  const scheduleCollapse = () => {
    if (!isMobile) return;
    if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
    collapseTimeoutRef.current = setTimeout(() => setExpanded(false), 350);
  };
  const wrap = (fn) => (...args) => {
    fn(...args);
    scheduleCollapse();
  };

  if (isMobile && !expanded) {
    // Bottom-left of the court on mobile — reveals the toolstrip on tap.
    // Basketball hoop + arrow icon signals "sport tools" cleverly.
    return (
      <button
        type="button"
        style={{ ...styles.strip, ...styles.collapsedButton }}
        onClick={() => setExpanded(true)}
        title="Show tools"
        aria-label="Show tools"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="11" r="7" />
          <path d="M5 11h14M12 4v14" />
          <path d="M7.05 6.05a10 10 0 0 0 9.9 9.9M16.95 6.05a10 10 0 0 1 -9.9 9.9" />
        </svg>
      </button>
    );
  }

  return (
  <div
    style={styles.strip}
    onMouseLeave={() => { if (isMobile) scheduleCollapse(); }}
  >
    <ToolButton
      id="select"
      icon={icons.select}
      active={activeTool === 'select'}
      title="Select (V)"
      onClick={wrap(() => onToolChange('select'))}
    />
    <ToolButton
      id="move"
      icon={icons.move}
      active={activeTool === 'move'}
      title="Move Player (M)"
      onClick={wrap(() => onToolChange('move'))}
    />
    <ToolButton
      id="eraser"
      icon={icons.eraser}
      active={activeTool === 'eraser'}
      title="Eraser (E)"
      onClick={wrap(() => onToolChange('eraser'))}
    />

    <div style={styles.divider} />

    <ToolButton
      id="undo"
      icon={icons.undo}
      disabled={!canUndo}
      title="Undo (Cmd+Z)"
      onClick={wrap(onUndo)}
    />
    <ToolButton
      id="redo"
      icon={icons.redo}
      disabled={!canRedo}
      title="Redo (Cmd+Shift+Z)"
      onClick={wrap(onRedo)}
    />

    <div style={styles.divider} />

    {!hideDefenderToggle && (
      <ToolButton
        id="defenders"
        icon={icons.shield}
        active={showDefenders}
        title="Toggle Defense (D)"
        onClick={wrap(onToggleDefenders)}
      />
    )}
    <ToolButton
      id="grid"
      icon={icons.grid}
      active={showGrid}
      title="Toggle Grid (G)"
      onClick={wrap(onToggleGrid)}
    />

    {onSettings && (
      <>
        <div style={styles.divider} />
        <ToolButton
          id="settings"
          icon={icons.gear}
          title="Settings"
          onClick={wrap(onSettings)}
        />
      </>
    )}
  </div>
  );
};

export default ToolStrip;
